import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';

export interface ConceptMastery {
  userId: string;
  courseId: string;
  conceptId: string;
  masteryScore: number;
  confidenceLevel: 'low' | 'medium' | 'high';
  lastInteraction: any;
  nextReviewAt?: any;
  reviewStep: number;
  history: { score: number; timestamp: any }[];
}

export interface LearningObjective {
  userId: string;
  courseId: string;
  lessonId: string;
  objectiveId: string;
  text: string;
  status: 'incomplete' | 'partial' | 'completed';
  masteryScore: number;
  relatedConceptId?: string;
  updatedAt: any;
}

export type MasteryUpdateAction = 'correct_quiz' | 'incorrect_quiz' | 'repeated_explanation' | 'correct_followup';

interface ScoringAdjustment {
  base: number;
  difficultyMultiplier: Record<string, number>;
  typeMultiplier: Record<string, number>;
}

const SCORING_CONFIG: Record<MasteryUpdateAction, ScoringAdjustment> = {
  correct_quiz: {
    base: 15,
    difficultyMultiplier: { easy: 0.8, medium: 1.0, hard: 1.5 },
    typeMultiplier: { general: 1.0, scenario: 1.2, application: 1.3, reasoning: 1.4 }
  },
  incorrect_quiz: {
    base: -10,
    difficultyMultiplier: { easy: 1.2, medium: 1.0, hard: 0.5 },
    typeMultiplier: { general: 1.0, scenario: 0.8, application: 0.7, reasoning: 0.6 }
  },
  repeated_explanation: {
    base: -5,
    difficultyMultiplier: { easy: 1.0, medium: 1.0, hard: 1.0 },
    typeMultiplier: { general: 1.0 }
  },
  correct_followup: {
    base: 10,
    difficultyMultiplier: { easy: 1.0, medium: 1.0, hard: 1.0 },
    typeMultiplier: { general: 1.0 }
  }
};

const REINFORCEMENT_INTERVALS = [
  3600 * 1000, // 1 hour
  24 * 3600 * 1000, // 24 hours
  3 * 24 * 3600 * 1000 // 3 days
];

export async function updateConceptMastery(
  userId: string,
  courseId: string,
  conceptId: string,
  action: MasteryUpdateAction,
  metadata: { difficulty?: string; type?: string } = {}
) {
  const safeConceptId = conceptId || 'unknown_concept';
  const masteryId = `${userId}_${courseId}_${safeConceptId.replace(/\s+/g, '_')}`;
  const masteryRef = doc(db, 'user_concept_mastery', masteryId);
  
  const snap = await getDoc(masteryRef);
  let currentMastery: ConceptMastery;

  if (snap.exists()) {
    currentMastery = snap.data() as ConceptMastery;
  } else {
    currentMastery = {
      userId,
      courseId,
      conceptId: safeConceptId,
      masteryScore: 50,
      confidenceLevel: 'medium',
      lastInteraction: new Date(),
      reviewStep: 0,
      history: []
    };
  }

  const config = SCORING_CONFIG[action];
  const diffMult = metadata.difficulty ? (config.difficultyMultiplier[metadata.difficulty as any] || 1) : 1;
  const typeMult = metadata.type ? (config.typeMultiplier[metadata.type as any] || 1) : 1;
  
  const scoreChange = config.base * diffMult * typeMult;
  let newScore = Math.max(0, Math.min(100, currentMastery.masteryScore + scoreChange));
  
  const newConfidence = calculateConfidenceLevel(newScore);
  
  let nextReviewAt = currentMastery.nextReviewAt;
  let newReviewStep = currentMastery.reviewStep;

  if (action === 'correct_quiz' || action === 'correct_followup') {
    const now = Date.now();
    nextReviewAt = new Date(now + REINFORCEMENT_INTERVALS[newReviewStep]);
    newReviewStep = (newReviewStep + 1) % REINFORCEMENT_INTERVALS.length;
  }

  const updatedHistory = [...(currentMastery.history || []), { score: newScore, timestamp: new Date() }].slice(-10);

  const updatedMastery: Partial<ConceptMastery> = {
    masteryScore: newScore,
    confidenceLevel: newConfidence,
    lastInteraction: serverTimestamp(),
    nextReviewAt: nextReviewAt ? (nextReviewAt instanceof Date ? nextReviewAt : nextReviewAt) : null,
    reviewStep: newReviewStep,
    history: updatedHistory,
    userId,
    courseId,
    conceptId: safeConceptId
  };

  await setDoc(masteryRef, updatedMastery, { merge: true });
  await updateGlobalLearningProfile(userId);
  return updatedMastery;
}

export async function updateLearningObjective(
  userId: string,
  courseId: string,
  lessonId: string,
  objective: { id: string; text: string; relatedConceptId?: string },
  masteryScore: number
) {
  const objectiveId = `${userId}_${courseId}_${lessonId}_${(objective.id || '').replace(/\s+/g, '_')}`;
  const objectiveRef = doc(db, 'user_learning_objectives', objectiveId);
  
  const status = masteryScore >= 80 ? 'completed' : (masteryScore >= 40 ? 'partial' : 'incomplete');
  
  await setDoc(objectiveRef, {
    userId,
    courseId,
    lessonId,
    objectiveId: objective.id,
    text: objective.text,
    status,
    masteryScore,
    relatedConceptId: objective.relatedConceptId || null,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

function calculateConfidenceLevel(score: number): 'low' | 'medium' | 'high' {
  if (score < 40) return 'low';
  if (score < 75) return 'medium';
  return 'high';
}

export async function updateGlobalLearningProfile(userId: string) {
  const profileRef = doc(db, 'user_learning_profiles', userId);
  
  const masteryRef = collection(db, 'user_concept_mastery');
  const q = query(masteryRef, where('userId', '==', userId));
  const snap = await getDocs(q);
  
  if (snap.empty) return;

  const masteries = snap.docs.map(d => d.data() as ConceptMastery);
  const avgScore = masteries.reduce((acc, curr) => acc + curr.masteryScore, 0) / masteries.length;
  
  // Momentum Calculation
  const latestHistories = masteries.flatMap(m => m.history || []);
  const improvementRate = latestHistories.length >= 2 
    ? latestHistories[latestHistories.length - 1].score - latestHistories[0].score
    : 0;
  
  const sessionCount = masteries.length; 
  const momentumScore = Math.min(100, (avgScore * 0.6) + (improvementRate * 2) + (sessionCount * 0.5));

  const sorted = [...masteries].sort((a, b) => b.masteryScore - a.masteryScore);
  const strongDomains = sorted.slice(0, 3).map(m => m.conceptId);
  const weakDomains = sorted.slice(-3).reverse().map(m => m.conceptId);

  await setDoc(profileRef, {
    userId,
    averageMasteryScore: avgScore,
    strongDomains,
    weakDomains,
    momentum: {
      score: momentumScore,
      improvementRate,
      sessionCount,
      consistency: improvementRate >= 0 ? 1 : 0.5
    },
    updatedAt: serverTimestamp()
  }, { merge: true });
}

export async function getMasteryForLesson(userId: string, courseId: string, concepts: any[]) {
  const scores: Record<string, number> = {};
  
  for (const concept of concepts) {
    const conceptSlug = (concept.concept_title || '').replace(/\s+/g, '_');
    const masteryId = `${userId}_${courseId}_${conceptSlug}`;
    const snap = await getDoc(doc(db, 'user_concept_mastery', masteryId));
    if (snap.exists()) {
      scores[concept.concept_title] = snap.data().masteryScore;
    } else {
      scores[concept.concept_title] = 50; 
    }
  }
  
  return scores;
}
